/**
 * AppDrawer - Responsive drawer component
 * 
 * Mobile: Bottom sheet with drag-to-close
 * Desktop: Side drawer
 * 
 * Supports dirty state tracking: when `isDirty` is true,
 * closing attempts show a discard confirmation before actually closing.
 */

import React, { createContext, useContext, useRef, useState, useCallback, useEffect } from "react";
import {
  Drawer,
  Group,
  Button,
  Text,
  Box,
  Alert,
  LoadingOverlay,
  ScrollArea,
  Modal,
  Tooltip,
  MantineSize,
} from "@mantine/core";
import { useTranslation } from "react-i18next";

import { useAppEnvironment } from "../AppEnvironmentContext";
import { useOverlayStack } from "../overlayStack";

import { AppIcon } from "../AppIcon";

// ============================================
// Types
// ============================================

export type DrawerActionType = "save-cancel" | "confirm-cancel" | "close" | "custom";

export interface AppDrawerAction {
  label: string;
  onClick: () => void;
  variant?: "primary" | "secondary" | "danger";
  iconName?: string;
  disabled?: boolean;
  loading?: boolean;
}

interface AppDrawerBaseProps {
  opened: boolean;
  onClose: () => void;
  title?: React.ReactNode;
  /**
   * The tag the title is drawn in. It is the drawer's HEADING either way (level 2, naming the dialog): `div` (default) is
   * a `role="heading"` box, for a title that is markup of its own (a group, a badge — a block inside an <h2> is invalid
   * HTML); `h2` is a real one, for a title that is only text (the public event site, owner 2026-10-24: "proper semantic
   * tags").
   */
  titleAs?: "div" | "h2";
  children?: React.ReactNode;
  closable?: boolean;
  /**
   * Hand focus back to what opened the drawer when it closes (Mantine's default, true). False when the close HANDS OVER
   * to another drawer opening at the same moment — returning focus would move it behind the new one.
   */
  returnFocus?: boolean;
  loading?: boolean;
  errors?: string | string[] | null;
  /**
   * True when the form has INLINE field errors. The drawer then adds a line to
   * the error alert above Save ("some fields need attention"), because in a long
   * scrolled form the red fields can be far off-screen and the save just looks
   * broken.
   *
   * ⚠ Pass `Object.values(fieldErrors).some(Boolean)` — NOT
   * `Object.keys(fieldErrors).length > 0`, which this comment used to say.
   * `getFormErrors` PRE-FILLS every known field name with `undefined`, so the keys
   * check is true the moment the form declares any known field: the alert then shows
   * "some fields need your attention" on a clean form with nothing highlighted.
   */
  hasFieldErrors?: boolean;
  /** Overrides the default field-error summary line. */
  fieldErrorsMessage?: string;
  onConfirm?: () => void;
  confirmLabel?: string;
  /** Keyboard-shortcut hint shown in the confirm/save button's tooltip (e.g. "⌘S"). */
  confirmShortcut?: string;
  confirmIcon?: string;
  confirmDisabled?: boolean;
  confirmColor?: string;
  cancelLabel?: string;
  customActions?: React.ReactNode;
  leftActions?: AppDrawerAction[];
  position?: "left" | "right" | "top" | "bottom";
  size?: MantineSize | string | number;
  responsive?: boolean;
  mobileHeightPercent?: number;
  mobileBreakpoint?: number;

  /**
   * On mobile, hug the content (grow only as tall as needed, up to ~90vh)
   * instead of the fixed `mobileHeightPercent` height. Use for short sheets
   * (filters, action lists) so they don't open near-full-height.
   */
  mobileFitContent?: boolean;

  /**
   * Extra style merged onto the drawer's CONTENT surface.
   *
   * The reason this exists: a Mantine Drawer portals to `document.body`, outside any themed subtree,
   * so on the public event site it would fall back to the base palette and look off-brand. Passing
   * the event's theme CSS vars here re-establishes them on the portaled surface, and the drawer's
   * own header/footer inherit from it. Not needed inside the dashboard, which has one theme.
   */
  contentStyle?: React.CSSProperties;
  /**
   * The "discard?" question's words, when the default ("Unsaved Changes … Keep Editing / Discard") does not suit the
   * reader — a guest half-way through buying tickets is not "editing" anything. Any part left out keeps the default.
   */
  discardText?: { title?: string; message?: string; keep?: string; discard?: string };
}

/**
 * A drawer with a Save / Confirm button holds a FORM, so it must say whether that form has changed (`isDirty`): when it
 * has, Esc, a click outside, Cancel, the X and a swipe down ask "Unsaved changes — discard?" before closing.
 *
 * ⚠⚠ REQUIRED for "save-cancel" / "confirm-cancel" (ROADMAP #123). It was optional and defaulted to false, so 28 form
 *   drawers (the guest drawer, guest group sets, tickets, discount codes …) never passed it and closed with the work in
 *   them, while 42 others asked — and nothing could tell the two apart. A form with genuinely nothing to lose passes
 *   `isDirty={false}` and says why. `useFormSnapshot` (apps/app) computes it.
 */
type AppDrawerActionProps =
  | { actionType: "save-cancel" | "confirm-cancel"; isDirty: boolean }
  | { actionType?: "close" | "custom"; /** When true, closing attempts show a discard confirmation */ isDirty?: boolean };

export type AppDrawerProps = AppDrawerBaseProps & AppDrawerActionProps;

// ============================================
// Leaving through the discard question
// ============================================

/**
 * Leave the drawer's current work — close it, or step back inside it — asking "Unsaved changes — discard?" first when
 * the drawer `isDirty`. Called with nothing it is the drawer's own close attempt (Cancel, the X, Esc); called with a
 * step (`() => setView("inbox")`) it runs that step instead of closing, after the same question.
 */
export type DrawerLeave = (step?: () => void) => void;

const DrawerLeaveContext = createContext<DrawerLeave | null>(null);

/**
 * The drawer's discard question, for a control the drawer does not render itself: a custom footer's Cancel, a Back
 * button in a step or a view, a title's ←. ⚠ Without it such a control calls `onClose` / a `setView` directly, past the
 * question, and drops the work (ROADMAP #124 — the ticket sale's Cancel, crew messages' Back, the top-up's card Back).
 * Usable only INSIDE an `AppDrawer` (its children, `title`, `customActions`) — it throws elsewhere, so a control moved
 * out of the drawer cannot silently lose the question. From the component that renders the drawer, use `<DiscardGuard>`.
 */
export const useDrawerLeave = (): DrawerLeave => {
  const leave = useContext(DrawerLeaveContext);

  if (!leave) {
    throw new Error("useDrawerLeave / DiscardGuard must be rendered inside an AppDrawer");
  }

  return leave;
};

/**
 * `useDrawerLeave` as a render prop, for JSX written in the component that renders the `AppDrawer` (where the hook
 * cannot reach the drawer's context): `<DiscardGuard>{(leave) => <Button onClick={() => leave(back)}>Back</Button>}</DiscardGuard>`.
 */
export const DiscardGuard: React.FC<{ children: (leave: DrawerLeave) => React.ReactNode }> = ({ children }) => (
  <>{children(useDrawerLeave())}</>
);

// ============================================
// Constants
// ============================================

const CLOSE_THRESHOLD = 100;

// ============================================
// Component
// ============================================

export const AppDrawer: React.FC<AppDrawerProps> = ({
  opened,
  onClose,
  title,
  titleAs = "div",
  children,
  closable = true,
  returnFocus = true,
  loading = false,
  errors,
  hasFieldErrors = false,
  fieldErrorsMessage,
  actionType = "close",
  onConfirm,
  confirmLabel,
  confirmShortcut,
  confirmIcon,
  confirmDisabled = false,
  confirmColor,
  cancelLabel,
  customActions,
  leftActions,
  position = "right",
  size = "lg",
  responsive = true,
  mobileHeightPercent = 92,
  mobileFitContent = false,
  isDirty = false,
  contentStyle,
  discardText,
}) => {
  const isTopOverlay = useOverlayStack(opened);
  // Every way out honours the same rule: not while `closable` is off, and not while a save runs (`loading`) — Cancel, the
  // X and the drag handle already refused mid-save, but Esc and a click outside did not, so the drawer closed and the
  // server's refusal landed on nothing (ROADMAP #121). AppModal has always done this.
  const canClose = closable && !loading;

  const { t } = useTranslation("common");

  // Responsive detection
  const { isMobile } = useAppEnvironment();
  const useBottomSheet = responsive && isMobile;

  // Drawer config based on viewport
  const drawerPosition = useBottomSheet ? "bottom" : position;
  const fitContent = useBottomSheet && mobileFitContent;
  const drawerSize = useBottomSheet ? `${mobileHeightPercent}%` : size;

  // Drag state
  const [dragY, setDragY] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [closingByDrag, setClosingByDrag] = useState(false);

  // Discard confirmation state
  const [discardConfirmOpened, setDiscardConfirmOpened] = useState(false);
  const isDiscardTop = useOverlayStack(discardConfirmOpened);
  // What "Discard" does: close the drawer (null), or the step a `leave(step)` asked for.
  const pendingStepRef = useRef<(() => void) | null>(null);

  const dragStartY = useRef(0);

  // Ref to Mantine's drawer content element for direct DOM manipulation
  const drawerContentRef = useRef<HTMLDivElement | null>(null);

  // ============================================
  // Guarded close: intercept if dirty
  // ============================================

  const handleCloseAttempt = useCallback(() => {
    pendingStepRef.current = null;
    if (isDirty) {
      setDiscardConfirmOpened(true);
    } else {
      onClose();
    }
  }, [isDirty, onClose]);

  // The same question for a control the drawer does not render (`useDrawerLeave`). Not mid-save, like every other way out;
  // and closing obeys `closable` too, as the X and Esc do (a step inside the drawer is not a close).
  const leave = useCallback<DrawerLeave>((step) => {
    if (loading) {
      return;
    }

    if (!step) {
      if (closable) {
        handleCloseAttempt();
      }

      return;
    }

    if (isDirty) {
      pendingStepRef.current = step;
      setDiscardConfirmOpened(true);
    } else {
      step();
    }
  }, [loading, closable, isDirty, handleCloseAttempt]);

  const handleConfirmDiscard = useCallback(() => {
    // Close modal first, then drawer after modal's close animation.
    // This lets Mantine restore scroll state on the drawer
    // before the drawer itself closes with its own animation.
    setDiscardConfirmOpened(false);
    const step = pendingStepRef.current;
    pendingStepRef.current = null;
    if (step) {
      step();
    } else {
      onClose();
    }
  }, [onClose]);

  const handleCancelDiscard = useCallback(() => {
    pendingStepRef.current = null;
    setDiscardConfirmOpened(false);
  }, []);

  // Reset discard modal when drawer closes
  useEffect(() => {
    if (!opened) {
      pendingStepRef.current = null;
      setDiscardConfirmOpened(false);
    }
  }, [opened]);

  // Find and store ref to Mantine's drawer content when opened
  // Don't set any styles initially - let Mantine's open animation play
  useEffect(() => {
    if (opened && useBottomSheet) {
      // Reset the closing flag
      setClosingByDrag(false);

      // ⚠ The content element is taken from the DRAG HANDLE when a drag starts (`handlePointerDown`) — never with a
      //   document-wide `querySelector('.mantine-Drawer-content')`, which found the FIRST open drawer in the page: with
      //   one sheet stacked on another (a policy over the RSVP form), dragging the top one moved the one beneath.
    }
    return;
  }, [opened, useBottomSheet]);

  // Apply transform directly to Mantine's drawer content during drag only
  useEffect(() => {
    const content = drawerContentRef.current;
    if (!content || !useBottomSheet || !isDragging) return;

    if (dragY > 0) {
      content.style.transitionDuration = '0ms';
      content.style.transform = `translateY(${dragY}px)`;
    }
  }, [dragY, isDragging, useBottomSheet]);

  // Handle snap-back animation when drag ends without closing
  useEffect(() => {
    const content = drawerContentRef.current;
    if (!content || !useBottomSheet) return;

    if (!isDragging && !closingByDrag && dragY === 0) {
      content.style.removeProperty('transform');
      content.style.removeProperty('transition-duration');
      content.style.removeProperty('transition-timing-function');
    }
  }, [isDragging, dragY, useBottomSheet, closingByDrag]);

  // Clean up ref when drawer closes
  useEffect(() => {
    if (!opened) {
      // Small delay to let close animation finish
      const timer = setTimeout(() => {
        if (drawerContentRef.current) {
          drawerContentRef.current.style.transform = '';
          drawerContentRef.current.style.transitionDuration = '';
        }

        drawerContentRef.current = null;
        setDragY(0);
        setIsDragging(false);
        setClosingByDrag(false);
      }, 300);
      return () => clearTimeout(timer);
    }
    return;
  }, [opened]);

  // ============================================
  // Drag Handlers using Pointer Events
  // (works for both touch and mouse)
  // ============================================

  const handlePointerDown = useCallback((e: React.PointerEvent) => {
    if (!closable || loading) return;

    // Capture pointer for tracking outside element
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
    // THIS sheet's content — the one whose handle is under the finger (see the open effect above).
    drawerContentRef.current = (e.currentTarget as HTMLElement).closest('.mantine-Drawer-content') as HTMLDivElement | null;

    dragStartY.current = e.clientY;
    setIsDragging(true);
    setDragY(0);
  }, [closable, loading]);

  const handlePointerMove = useCallback((e: React.PointerEvent) => {
    if (!isDragging) return;

    const delta = e.clientY - dragStartY.current;
    // Only allow dragging down
    if (delta > 0) {
      setDragY(delta);
    }
  }, [isDragging]);

  const handlePointerUp = useCallback((e: React.PointerEvent) => {
    if (!isDragging) return;

    // Release pointer capture
    (e.target as HTMLElement).releasePointerCapture(e.pointerId);

    setIsDragging(false);

    if (dragY > CLOSE_THRESHOLD) {
      // If dirty, snap back and show confirmation instead of closing
      if (isDirty) {
        setDragY(0);
        pendingStepRef.current = null;
        setDiscardConfirmOpened(true);
        return;
      }

      // Mark that we're closing by drag - prevents snap-back
      setClosingByDrag(true);

      // Animate drawer off-screen
      const content = drawerContentRef.current;
      if (content) {
        content.style.transitionDuration = '200ms';
        content.style.transitionTimingFunction = 'ease-out';
        content.style.transform = 'translateY(100%)';
      }

      // Close after animation completes
      setTimeout(() => {
        onClose();
      }, 200);
    } else {
      // Snap back to original position
      setDragY(0);
    }
  }, [isDragging, dragY, isDirty, onClose]);

  // ============================================
  // Error messages
  // ============================================

  const errorMessages = errors
    ? Array.isArray(errors) ? errors : [errors]
    : [];

  // Inline field errors get a pointer here too — a user who scrolled past them
  // would otherwise see Save do nothing with no visible reason.
  if (hasFieldErrors) {
    errorMessages.push(
      fieldErrorsMessage ?? "Some fields need your attention — check the highlighted fields above."
    );
  }

  // ============================================
  // Left actions renderer
  // ============================================

  const renderLeftActions = () => {
    if (!leftActions || leftActions.length === 0) return null;

    return (
      <Group gap="sm" wrap="nowrap">
        {leftActions.map((action, index) => {
          const buttonVariant = action.variant === "danger"
            ? "filled"
            : action.variant === "primary"
              ? "filled"
              : "default";

          const buttonColor = action.variant === "danger"
            ? "red"
            : action.variant === "primary"
              ? undefined
              : undefined;

          return (
            <Button
              key={index}
              variant={buttonVariant}
              color={buttonColor}
              size="lg"
              onClick={action.onClick}
              disabled={action.disabled || loading}
              loading={action.loading}
              leftSection={
                action.iconName && !action.loading ? (
                  <AppIcon iconName={action.iconName} size={18} />
                ) : undefined
              }
            >
              {action.label}
            </Button>
          );
        })}
      </Group>
    );
  };

  // ============================================
  // Action buttons renderer
  // ============================================

  const renderActions = () => {
    if (actionType === "custom") {
      return customActions;
    }

    if (actionType === "close") {
      return (
        <Button
          variant="default"
          size="lg"
          onClick={handleCloseAttempt}
          disabled={loading}
          fullWidth={useBottomSheet}
        >
          {cancelLabel || t("buttons.close", "Close")}
        </Button>
      );
    }

    const isSaveCancel = actionType === "save-cancel";
    const primaryLabel = confirmLabel || (isSaveCancel ? t("buttons.save", "Save") : t("buttons.confirm", "Confirm"));
    const secondaryLabel = cancelLabel || t("buttons.cancel", "Cancel");

    const confirmButton = (
      <Button
        onClick={onConfirm}
        loading={loading}
        disabled={confirmDisabled}
        size="lg"
        color={confirmColor}
        leftSection={confirmIcon && !loading ? <AppIcon iconName={confirmIcon} size={18} /> : undefined}
        style={useBottomSheet ? { flex: "1 1 0", minWidth: 0 } : undefined}
      >
        {primaryLabel}
      </Button>
    );

    // ⓘ On a phone the ACTION takes the width Cancel does not need (2026-10-15). Two equal halves (`grow`) left ~110px
    //   of label on a 375px screen, so "Close account", "Record payment", "Save decisions" … were cut mid-word.
    return (
      <Group justify="flex-end" gap="sm" wrap="nowrap">
        <Button variant="default" onClick={handleCloseAttempt} disabled={loading} size="lg" style={useBottomSheet ? { flex: "0 0 auto" } : undefined}>
          {secondaryLabel}
        </Button>
        {confirmShortcut ? (
          <Tooltip label={confirmShortcut} withArrow position="top" openDelay={400}>
            {confirmButton}
          </Tooltip>
        ) : (
          confirmButton
        )}
      </Group>
    );
  };

  // A "custom" footer with nothing to show → no footer at all (e.g. an action
  // list sheet). Otherwise show the footer for save/confirm/close types.
  const showActions =
    actionType === "custom"
      ? Boolean(customActions)
      : actionType !== "close" || closable;
  const hasLeftActions = leftActions && leftActions.length > 0;

  // Calculate visual feedback values
  const dragProgress = Math.min(dragY / CLOSE_THRESHOLD, 1);
  const handleScale = 1 + (dragProgress * 0.3);
  // From the surface's own ink (`currentColor`), so the handle shows on a DARK surface too — a fixed black at 20% vanished
  // on the public site's dark event themes (review 2026-10-05).
  const handleColor = `color-mix(in srgb, currentColor ${Math.round((isDragging ? 0.25 + dragProgress * 0.3 : 0.25) * 100)}%, transparent)`;

  // ============================================
  // Render
  // ============================================

  return (
    <DrawerLeaveContext.Provider value={leave}>
      <Drawer
        opened={opened}
        onClose={handleCloseAttempt}
        position={drawerPosition}
        size={drawerSize}
        // Don't use Mantine's header - we render our own inside the transform wrapper
        title={undefined}
        withCloseButton={false}
        closeOnClickOutside={canClose && !isDragging}
        // ⚠ Only the TOP open drawer / modal closes on Esc — Mantine would close every one of them (see overlayStack.ts).
        // This is the drawer's ONLY Esc: `useModalShortcuts` registers none beside it (#121), so a dirty drawer always asks.
        closeOnEscape={canClose && isTopOverlay}
        returnFocus={returnFocus}
        padding={0}
        // Disable Mantine's transition when we're closing by drag (we handle it ourselves)
        transitionProps={{
          duration: closingByDrag ? 0 : 200,
        }}
        styles={{
          content: {
            ...contentStyle,
            borderTopLeftRadius: useBottomSheet ? 16 : 0,
            borderTopRightRadius: useBottomSheet ? 16 : 0,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            // Fit-content bottom sheet: override ONLY the height so the sheet
            // grows to its content (capped). Do NOT touch `flex` — Mantine uses
            // it for full width + bottom anchoring; overriding it makes the sheet
            // float and mis-size.
            ...(fitContent ? { height: "auto", maxHeight: "90vh" } : {}),
          },
          header: {
            display: "none", // Hide Mantine's header completely
          },
          body: {
            padding: 0,
            // In fit-content mode the body must hug its content too, otherwise
            // flex:1 collapses against the auto-height content.
            flex: fitContent ? "0 1 auto" : 1,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            minHeight: 0,
          },
        }}
      >
        {/* Loading overlay */}
        <LoadingOverlay visible={loading} zIndex={1000} />

        {/* 
          Content wrapper - transform is applied directly to Mantine's 
          .mantine-Drawer-content via ref for proper visual feedback
        */}
        {/* ⚠ Fit-content: the sheet has NO height of its own (auto, capped at 90vh), so `height: 100%` here resolves to
            the CONTENT's height — the scroll area then grew past the cap and was clipped instead of scrolling (the
            Share drawer's lower social buttons could not be reached on a phone, 2026-10-09). So it SHRINKS with the
            sheet instead: `flex` + `minHeight: 0` all the way down to the scroll area. */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            ...(fitContent ? { flex: "0 1 auto", minHeight: 0 } : { height: "100%" }),
          }}
        >

          {/* ====== DRAG HANDLE (mobile only) ====== */}
          {useBottomSheet && (
            <Box
              onPointerDown={handlePointerDown}
              onPointerMove={handlePointerMove}
              onPointerUp={handlePointerUp}
              onPointerCancel={handlePointerUp}
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                padding: "12px 0 8px 0",
                cursor: closable && !loading ? "grab" : "default",
                touchAction: "none",
                userSelect: "none",
                flexShrink: 0,
              }}
            >
              {/* Handle bar with visual feedback */}
              <Box
                style={{
                  width: 40,
                  height: 5,
                  borderRadius: 3,
                  backgroundColor: handleColor,
                  transform: `scaleX(${handleScale})`,
                  transitionProperty: isDragging ? "none" : "transform, background-color",
                  transitionDuration: "200ms",
                  transitionTimingFunction: "ease-out",
                }}
              />
              {/* Hint text when dragging past threshold */}
              {dragY > CLOSE_THRESHOLD * 0.7 && (
                <Text
                  size="xs"
                  c="dimmed"
                  mt={4}
                  style={{
                    opacity: Math.min((dragY - CLOSE_THRESHOLD * 0.7) / (CLOSE_THRESHOLD * 0.3), 1),
                    transitionProperty: "opacity",
                    transitionDuration: "100ms",
                  }}
                >
                  Release to close
                </Text>
              )}
            </Box>
          )}

          {/* ====== CUSTOM HEADER (title + close button) ====== */}
          {(title || closable) && (
            <Box
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "16px",
                paddingTop: useBottomSheet ? 8 : 16,
                flexShrink: 0,
              }}
            >
              {/* A div, not Mantine's default <p>: a title may be markup of its own (a group, a badge) — a block inside a
                  <p> is invalid HTML and React logs it (review 2026-10-05).
                  ⓘ 2026-10-24 — Mantine's Drawer.Title, so the dialog is NAMED by it (`aria-labelledby`) and it is a
                  level-2 heading: a real <h2> when `titleAs="h2"`, else a div with the heading role. Its own look (16px,
                  weight 400, line-height 1) is cleared — the Text inside draws the title as before. */}
              <Drawer.Title
                component={titleAs}
                {...(titleAs === "div" ? { role: "heading", "aria-level": 2 } : {})}
                style={{ margin: 0, padding: 0, font: "inherit", lineHeight: "inherit", minWidth: 0 }}
              >
                <Text component={titleAs === "h2" ? "span" : "div"} fw={600} size="lg" style={titleAs === "h2" ? { display: "block" } : undefined}>
                  {title}
                </Text>
              </Drawer.Title>
              {closable && (
                <Box
                  component="button"
                  onClick={handleCloseAttempt}
                  disabled={loading}
                  style={{
                    background: "none",
                    border: "none",
                    cursor: loading ? "default" : "pointer",
                    padding: 4,
                    borderRadius: 4,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    // The surface's dimmed ink — a fixed gray was faint on a dark event theme.
                    color: "var(--mantine-color-dimmed)",
                    opacity: loading ? 0.5 : 1,
                  }}
                  aria-label="Close"
                >
                  <AppIcon iconName="X" size={20} />
                </Box>
              )}
            </Box>
          )}

          {/* ====== SCROLLABLE CONTENT using Mantine ScrollArea ====== */}
          {/* ⚠ Fit-content: Mantine's viewport is `height: 100%`, which an auto-height sheet cannot resolve — so the
              viewport is a flex child of the scroll area too, and shrinks to it. */}
          <ScrollArea
            style={{ flex: fitContent ? "0 1 auto" : 1, minHeight: 0, ...(fitContent ? { display: "flex", flexDirection: "column" } : {}) }}
            styles={fitContent ? { viewport: { flex: "0 1 auto", minHeight: 0, height: "auto" } } : undefined}
            type="auto"
            offsetScrollbars
            scrollbarSize={8}
          >
            <Box px="md" pb="md">
              {children}
            </Box>
          </ScrollArea>

          {/* ====== ERROR MESSAGES ====== */}
          {errorMessages.length > 0 && (
            <Box px="md" style={{ flexShrink: 0 }}>
              <Alert
                color="red"
                icon={<AppIcon iconName="AlertCircle" size={16} />}
                mb="md"
              >
                {errorMessages.map((msg, i) => (
                  <Text key={i} size="md">{msg}</Text>
                ))}
              </Alert>
            </Box>
          )}

          {/* ====== ACTION BUTTONS (sticky footer) ====== */}
          {showActions && (
            <Box
              style={{
                flexShrink: 0,
                borderTop: "1px solid var(--mantine-color-gray-3)",
                padding: 16,
                paddingBottom: useBottomSheet
                  ? "calc(16px + env(safe-area-inset-bottom, 0px))"
                  : 16,
                backgroundColor: "var(--mantine-color-body)",
              }}
            >
              {hasLeftActions ? (
                <Group justify="space-between" wrap="nowrap">
                  {renderLeftActions()}
                  {renderActions()}
                </Group>
              ) : (
                renderActions()
              )}
            </Box>
          )}

        </div>
      </Drawer>

      {/* ====== DISCARD CONFIRMATION MODAL ====== */}
      <Modal
        opened={discardConfirmOpened}
        onClose={handleCancelDiscard}
        // On the overlay stack too: Esc on "Unsaved changes" closes the question, not the drawer behind it.
        closeOnEscape={isDiscardTop}
        title={discardText?.title ?? t("unsavedChanges.title", "Unsaved Changes")}
        centered
        size="sm"
        zIndex={1000}
        // ⚠ The question portals out on its own, like the drawer — it needs the same surface (`contentStyle`: on the event
        //   site, the event's theme variables, background and ink), or on a dark event theme it paints base colours.
        styles={contentStyle ? { content: contentStyle, header: contentStyle } : undefined}
      >
        <Text size="md" mb="lg">
          {discardText?.message ??
            t(
              "unsavedChanges.message",
              "You have unsaved changes. Are you sure you want to discard them?"
            )}
        </Text>
        <Group justify="flex-end" gap="sm">
          <Button variant="default" onClick={handleCancelDiscard}>
            {discardText?.keep ?? t("unsavedChanges.keepEditing", "Keep Editing")}
          </Button>
          <Button color="red" onClick={handleConfirmDiscard}>
            {discardText?.discard ?? t("unsavedChanges.discard", "Discard")}
          </Button>
        </Group>
      </Modal>
    </DrawerLeaveContext.Provider>
  );
};